import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../../firebase/firebase.js";

const functions = getFunctions(app);
const auth = getAuth(app);
const ROUTE_AND_PROCESS_HTTP_ENDPOINT =
  "https://us-central1-assistant-49bb1.cloudfunctions.net/routeAndProcessMessageHttp";
const SEND_MESSAGE_HTTP_ENDPOINT =
  "https://us-central1-assistant-49bb1.cloudfunctions.net/sendMessageToChatGPTHttp";

/**
 * Sends a chat request to the Cloud Function.
 * @param {{ agentId:string, userMessage:string, data:Object }} params
 * @returns {Promise<Object>} full response from CF (includes `.message`)
 */
export async function sendMessageToChatGPT({ agentId, userMessage = "" }) {
  if (!agentId) {
    throw new Error("agentId is required");
  }

  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(SEND_MESSAGE_HTTP_ENDPOINT, {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ agentId, userMessage }),
      });

      let payload;
      try {
        payload = await response.json();
      } catch (parseError) {
        const error = new Error("Invalid JSON response from sendMessageToChatGPTHttp.");
        error.cause = parseError;
        error.status = response.status;
        throw error;
      }

      if (!response.ok) {
        const message =
          payload?.error?.message ||
          `sendMessageToChatGPTHttp failed with status ${response.status}.`;
        const error = new Error(message);
        error.status = response.status;
        error.response = payload;
        throw error;
      }

      if (typeof payload?.result === "undefined") {
        throw new Error(
          "sendMessageToChatGPTHttp returned an unexpected payload."
        );
      }

      return payload.result;
    } catch (httpErr) {
      console.warn(
        "sendMessageToChatGPT HTTP call failed, attempting callable fallback",
        httpErr
      );
    }
  }

  const fn = httpsCallable(functions, "sendMessageToChatGPT");
  const res = await fn({ agentId, userMessage });
  return res.data; // { message, improvements, … }
}

/**
 * Sends a request to a coordinator agent.
 * @param {{ coordinatorId:string, userMessage:string }} params
 * @returns {Promise<Object>} full response from CF
 */
export async function sendMessageToCoordinator({
  coordinatorId,
  userMessage = "",
}) {
  const fn = httpsCallable(functions, "sendMessageToCoordinator");
  const res = await fn({ coordinatorId, userMessage });
  return res.data;
}

/**
 * Generates an image via Cloud Function proxy.
 */
export async function generateImage(
  prompt,
  size = "1024x1024",
  quality = "standard"
) {
  const fn = httpsCallable(functions, "generateImage");
  const res = await fn({ description: prompt, size, quality });
  return res.data.b64_json;
}

/**
 * Transcribes audio via Cloud Function proxy (Whisper).
 *
 * @param {Blob} audioBlob audio in a format Whisper supports
 * @returns {Promise<Object>} data from the Cloud Function ({ text: string })
 */
export async function sendAudioToGemini(audioBlob) {
  const reader = new FileReader();
  const b64 = await new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(audioBlob);
  });
  const fn = httpsCallable(functions, "transcribeAudio");
  const res = await fn({
    audioB64: b64,
    fileName: "audio.webm",
    format: "text",
    mimeType: audioBlob.type || "audio/webm",
  });
  return res.data;
}

/**
 * Routes a message through the coordinator router which then
 * forwards it to the appropriate agent.
 * @param {{ userMessage:string, data:Object }} params
 * @returns {Promise<Object>} full response from CF
 */
async function callRouteAndProcessMessageHttp(userMessage) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("User must be signed in to contact the routing service.");
  }

  const idToken = await currentUser.getIdToken();
  const response = await fetch(ROUTE_AND_PROCESS_HTTP_ENDPOINT, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ userMessage }),
  });

  let payload;
  try {
    payload = await response.json();
  } catch (parseError) {
    const error = new Error("Invalid JSON response from routing function.");
    error.cause = parseError;
    error.status = response.status;
    throw error;
  }

  if (!response.ok) {
    const message =
      payload?.error?.message ||
      `Routing HTTP function failed with status ${response.status}.`;
    const error = new Error(message);
    error.status = response.status;
    error.response = payload;
    throw error;
  }

  if (typeof payload?.result === "undefined") {
    throw new Error("Routing HTTP function returned an unexpected payload.");
  }

  return payload.result;
}

export async function routeAndProcessMessage(userMessage = "") {
  let lastError;

  try {
    return await callRouteAndProcessMessageHttp(userMessage);
  } catch (httpErr) {
    lastError = httpErr;
    console.warn(
      "routeAndProcessMessage HTTP call failed, attempting manual routing",
      httpErr
    );
  }

  try {
    const routerRes = await sendMessageToCoordinator({
      coordinatorId: "coordinator_router",
      userMessage,
    });
    const targetAgentId =
      routerRes.targetAgentId?.trim() || routerRes.target_agent_id?.trim();
    if (!targetAgentId) {
      return {
        message:
          routerRes.reply ||
          routerRes.message ||
          "Could not determine how to route your request. Please try rephrasing.",
        routingError: true,
        routerResponse: routerRes,
      };
    }
    const finalRes = await sendMessageToChatGPT({
      agentId: targetAgentId,
      userMessage,
    });
    return { ...finalRes, agentId: targetAgentId };
  } catch (innerErr) {
    innerErr.previousError = lastError;
    throw innerErr;
  }
}
export async function embedText(text) {
  const fn = httpsCallable(functions, "generateEmbedding");
  const res = await fn({ text });
  return res.data.embedding;
}
